import { useContext } from 'react';
import { useNavigate, useLocation, matchPath } from 'react-router-dom';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Navbar } from 'react-bootstrap';
import { AppContext } from '../../context/AppContext';
import { HISTORY_ZERO_STATE } from '../../constants/HistoryConstants';
import { generateChatSessionId } from '../../utils/chat';
import { logout } from '../../services/authentication';
import { menuConfig } from '../home/menuConfig';
import { CommonDropdown } from '../common/UI/CommonDropdown';
import ExpandCollapseIcon from '../../assets/icon-hamburger.svg?react';
import PrivateChatIcon from '../../assets/icon-private-chat.svg?react';
import CloseIcon from '@/assets/icon-close.svg?react';

import ElyisaIcon from '@/assets/Elysia-logo.svg?react';
import './style.scss';
import { AuthContext } from '../../context/AuthContext';
import AppAgentHeading from '../app-builder/AppAgentHeading';
import useElysiaStore from '@/app/elysia-store';
import { SettingContext } from '@/context/SettingContext';
import AppActionsDropdown from '../app-builder/AppActionsDropdown';
import { DEFAULT_ASPECT_RATIO, DEFAULT_IMAGE_GEN_RESOLUTION } from '@/config/settings';

const Menu = ({ chatInputRef }) => {
  const navigate = useNavigate();

  const { setIsSideNavOpen, allowedRoutes = [] } = useContext(AppContext);
  const { userInitials = '' } = useContext(AuthContext);
  const location = useLocation();

  const isPreview = useElysiaStore((state) => state.isPreview);
  const isAppPreview = useElysiaStore((state) => state.isAppPreview);
  const aiAppId = useElysiaStore((state) => state.aiAppId);
  const chatAgent = useElysiaStore((state) => state.chatAgent);
  const isChatActive = useElysiaStore((state) => state.isChatActive);
  const isPrivateChat = useElysiaStore((state) => state.isPrivateChat);
  const appKey = useElysiaStore((state) => state.appKey);
  const promptToolsConfig = useElysiaStore((state) => state.promptToolsConfig);
  const isAppFileAttached = useElysiaStore((state) => state.isAppFileAttached);

  const setPromptInput = useElysiaStore.getState().setPromptInput;
  const setIsChatActive = useElysiaStore.getState().setIsChatActive;
  const setNewChat = useElysiaStore.getState().setNewChat;
  const setChatSessionId = useElysiaStore.getState().setChatSessionId;
  const setClearState = useElysiaStore.getState().setClearState;
  const setChatAgent = useElysiaStore.getState().setChatAgent;
  const setActiveChatHistoryRecord = useElysiaStore.getState().setActiveChatHistoryRecord;
  const setSessionPath = useElysiaStore.getState().setSessionPath;
  const setIsRestrictedChatSessionStatus =
    useElysiaStore.getState().setIsRestrictedChatSessionStatus;
  const setIsRestrictedAppStatus = useElysiaStore.getState().setIsRestrictedAppStatus;
  const setShowScrollIcon = useElysiaStore.getState().setShowScrollIcon;
  const setUploadedFileTitle = useElysiaStore.getState().setUploadedFileTitle;
  const setUploadedFile = useElysiaStore.getState().setUploadedFile;
  const setSources = useElysiaStore.getState().setSources;
  const setSelectedSources = useElysiaStore.getState().setSelectedSources;
  const setWordsCount = useElysiaStore.getState().setWordsCount;
  const setWordCountError = useElysiaStore.getState().setWordCountError;
  const setShowFileToasts = useElysiaStore.getState().setShowFileToasts;
  const setIsChatDisabled = useElysiaStore.getState().setIsChatDisabled;
  const setIsAppTerminated = useElysiaStore.getState().setIsAppTerminated;
  const setBrandDoc = useElysiaStore.getState().setBrandDoc;
  const setCategoryCount = useElysiaStore.getState().setCategoryCount;
  const setIsAddSourcesActive = useElysiaStore.getState().setIsAddSourcesActive;
  const setImageGenPayload = useElysiaStore.getState().setImageGenPayload;
  const setProfitCenter = useElysiaStore.getState().setProfitCenter;
  const setFinanceAppLocation = useElysiaStore.getState().setFinanceAppLocation;
  const resetAIApp = useElysiaStore.getState().resetAIApp;
  const setAttachfileArray = useElysiaStore((state) => state.setAttachedFileArray);
  const setChatInputSources = useElysiaStore((state) => state.setChatInputSources);
  const { totalImageQuota, availableImageQuota, defaultModel, setSelectedModel } =
    useContext(SettingContext);

  const isDashboardRouteAllowed = allowedRoutes?.includes('/dashboard');
  const isSandboxRouteAllowed = allowedRoutes?.includes('/sandbox');

  const config = menuConfig(
    navigate,
    logout,
    userInitials,
    isSandboxRouteAllowed,
    isDashboardRouteAllowed
  );
  const userMenu = config.logoutMenu;
  const showControls =
    location.pathname === '/' ||
    location.pathname === '/session' ||
    location.pathname.startsWith('/session/');

  // Remove "View Profile" and "Sandbox" options from the user menu on small screens
  if (userMenu) {
    userMenu.dropdownItems =
      userMenu?.dropdownItems.filter(
        (item) => item.text !== 'View Profile' && item.text !== 'Sandbox'
      ) || [];
  }

  const privateChatAllowedRoutes = ['/', '/session/:sessionid', '/app=:agentId'];

  const isPrivateChatRoute = privateChatAllowedRoutes.some((route) =>
    matchPath({ path: route, end: true }, location.pathname)
  );

  const startNewTopicChat = (exitApp = false) => {
    setIsAddSourcesActive?.(false);
    useElysiaStore.setState({ isStreamingStopped: false });
    setClearState?.(true);
    setNewChat?.(true);
    setChatSessionId?.(generateChatSessionId());
    if (chatAgent !== 'app-agent' || exitApp) {
      setChatAgent?.('Generic');
      setIsAppTerminated(true);
      resetAIApp();
      setSelectedModel(defaultModel);
      setBrandDoc(false);
    }
    setIsChatActive?.(false);
    setShowScrollIcon?.(false);
    setActiveChatHistoryRecord(HISTORY_ZERO_STATE);
    navigate('/', { replace: true });
    setPromptInput('');
    setSessionPath?.('');
    setIsRestrictedChatSessionStatus?.('');
    setIsRestrictedAppStatus('');
    setUploadedFileTitle?.('');
    setUploadedFile?.(null);
    setAttachfileArray?.([]);
    if (!isAppFileAttached) {
      setSources?.([]);
      setSelectedSources?.([]);
      setChatInputSources?.([]);
      setBrandDoc(false);
    }
    setWordCountError?.(false);
    setWordsCount?.(0);
    setShowFileToasts?.(false);
    setIsChatDisabled?.(false);
    setCategoryCount({});
    setImageGenPayload({
      aspect_ratio: DEFAULT_ASPECT_RATIO,
      color_palette: [],
      starter_image_id: '',
      image_quality: DEFAULT_IMAGE_GEN_RESOLUTION,
      starter_image_file_name: '',
    });
    setProfitCenter({ value: '', label: '', level: 0, id: '' });
    setFinanceAppLocation({ value: '', label: '', level: 0, id: '' });
  };

  const OpenSideNav = (e) => {
    setIsSideNavOpen(true);
    e.stopPropagation();
  };

  const handlePrivateChatClick = () => {
    chatInputRef.current?.togglePrivateChat();
  };

  const MobileMenu = () => {
    return (
      <Button
        aria-label="side panel"
        data-testid="side-panel-icon-button-mobile"
        className="collapse-btn-wrapper fw-semibold"
        onClick={OpenSideNav}
      >
        <ExpandCollapseIcon />
      </Button>
    );
  };

  const showBanner =
    isPrivateChat && (location.search.includes('private-chat') || location.pathname == '/');

  const privateChatButton = !isPreview &&
    !isAppPreview &&
    promptToolsConfig.privateChat &&
    isPrivateChatRoute && (
      <Button
        className={`private-chat-btn ${isPrivateChat ? 'selected' : ''}`}
        onClick={handlePrivateChatClick}
      >
        <PrivateChatIcon />
      </Button>
    );
  return (
    <>
      {showBanner ? (
        <Navbar className="private-chat-navbar px-3">
          <div className="w-100 d-flex align-items-center justify-content-between">
            <span className="private-chat-navbar-title">Private Chat</span>

            <Button
              variant="link"
              className="private-chat-navbar-close p-0"
              aria-label="Close private chat"
              onClick={handlePrivateChatClick}
            >
              <CloseIcon />
            </Button>
          </div>
        </Navbar>
      ) : (
        <>
          <Navbar className="d-none d-lg-flex d-md-none justify-content-end pe-2">
            {privateChatButton}
          </Navbar>

          <Navbar className="custom-nav d-flex d-lg-none justify-content-between align-items-center">
            <div className="b-block d-lg-none open-sidebar-mobile">
              <MobileMenu />
            </div>
            <div className="d-md-flex d-sm-none d-none">{privateChatButton}</div>
            <Navbar.Brand className="flex items-center cursor-pointer mobile-branding">
              <Button
                className="gemini-logo"
                variant="link"
                onClick={() => startNewTopicChat(true)}
              >
                <ElyisaIcon />
              </Button>
            </Navbar.Brand>

            <div className="profile-menu d-md-none">
              {userMenu && <CommonDropdown {...userMenu} />}
            </div>
          </Navbar>
        </>
      )}

      {showControls && aiAppId && (
        <div className="flex-grow-1 d-flex justify-content-center me-2 mt-2 position-relative">
          <div className="width-control d-flex justify-content-between">
            <div>
              {chatAgent === 'app-agent' && !isPreview && !isAppPreview && isChatActive && (
                <div className="d-flex align-items-center">
                  <AppAgentHeading />
                  <AppActionsDropdown aiAppId={aiAppId} startNewTopicChat={startNewTopicChat} />
                </div>
              )}
            </div>
          </div>

          {appKey === 'image_generator' && (
            <div className="position-absolute end-0">
              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip className="tooltip-sidebar">
                    You can generate up to {totalImageQuota} images per calendar month. The count
                    resets at the start of each month, and unused images do not roll over.
                  </Tooltip>
                }
              >
                <Button className="counter-btn me-2">
                  {availableImageQuota}/{totalImageQuota}
                </Button>
              </OverlayTrigger>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default Menu;
